import { Progress } from "@base-ui/react/progress";
import clsx from "clsx";
import type React from "react";
import { forwardRef } from "react";
import { vars } from "../../theme/theme.css";
import { spinnerStyles } from "./styles.css";

interface SpinnerProps extends React.HTMLAttributes<HTMLDivElement> {
  color?: string;
  size?: number;
  /** When provided, renders a determinate progress bar with role="progressbar" and aria-valuenow. */
  value?: number;
  max?: number;
}

const SVG_MASK =
  "url(\"data:image/svg+xml, <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'><path d='M9.249 0.645999C9.63764 0.588245 10.0334 0.685283 10.3513 0.916254C10.6691 1.14722 10.8837 1.49369 10.9488 1.88116C11.0139 2.26864 10.9243 2.66619 10.6994 2.98835C10.4745 3.31051 10.1322 3.53157 9.746 3.604C7.99897 3.90027 6.41326 4.80555 5.26996 6.15934C4.12666 7.51314 3.49963 9.22802 3.5 11C3.5 12.9891 4.29018 14.8968 5.6967 16.3033C7.10322 17.7098 9.01088 18.5 11 18.5V21.5C5.201 21.5 0.5 16.8 0.5 11C0.5 5.855 4.226 1.49 9.249 0.645999ZM15.892 3.29799C16.0235 3.15124 16.1827 3.03185 16.3604 2.94664C16.5381 2.86143 16.7309 2.81208 16.9277 2.8014C17.1245 2.79072 17.3214 2.81892 17.5073 2.8844C17.6932 2.94987 17.8643 3.05134 18.011 3.18299C19.11 4.16702 19.9888 5.37197 20.59 6.71901C21.1913 8.06606 21.5013 9.52486 21.5 11C21.5 16.8 16.799 21.5 11 21.5V18.5C12.5182 18.5 14.0006 18.0393 15.2513 17.1788C16.502 16.3182 17.4621 15.0984 18.0047 13.6805C18.5473 12.2626 18.6469 10.7135 18.2902 9.23782C17.9335 7.76216 17.1373 6.42949 16.007 5.41599C15.7109 5.15036 15.5325 4.778 15.5109 4.38081C15.4893 3.98361 15.6264 3.59412 15.892 3.29799Z'/></svg>\")";

export const Spinner = forwardRef(function Spinner(
  { color, size, value, max = 100, className, ...props }: SpinnerProps,
  ref: React.Ref<HTMLDivElement>,
) {
  const resolvedColor = color || vars.colors.primary.default;
  const resolvedSize = size || 22;

  const spinnerDiv = (
    <div
      ref={ref}
      className={clsx(spinnerStyles, className)}
      {...props}
      style={{
        width: `${resolvedSize}px`,
        height: `${resolvedSize}px`,
        backgroundImage: `conic-gradient(${resolvedColor} 0%, transparent 5%, transparent 10%, ${resolvedColor} 100%)`,
        maskImage: SVG_MASK,
        maskSize: "contain",
        maskRepeat: "no-repeat",
        maskPosition: "center",
        ...props.style,
      }}
    />
  );

  if (value !== undefined) {
    return (
      <Progress.Root value={value} max={max}>
        <Progress.Track style={{ display: "contents" }}>
          <Progress.Indicator style={{ display: "contents" }}>
            {spinnerDiv}
          </Progress.Indicator>
        </Progress.Track>
      </Progress.Root>
    );
  }

  return spinnerDiv;
});

Spinner.displayName = "Spinner";
